import { EntityType } from '../components/DTStudio/interfaces'
import {
    AttributeType,
    EntityStatus,
    TemplatePermissionMode,
} from '../constants/constants'
import {
    IEntityRecord,
    IFilterValue,
    IGlobalSearchTextFilter,
    ITableFilters,
    ColumnFilters,
    IEntityStatusFilter,
} from '../components/Entity/interfaces'
import {
    IEntityInstanceRelationsResponse,
    RelatedEntityInstance,
} from '../services/apiInterfaces'
import { ColumnOrderListType } from '../redux/slices/entitySlice'

export const getEntityId = (
    entityList: EntityType[],
    entityName: string,
): string | undefined => {
    const entity = entityList.find((entity) => entity.name === entityName)
    return entity?.id
}

/**
 * This will find any entity at any nested level
 * @param entityTypes
 * @param id
 * @returns
 */
export const findEntityById = (
    entityTypes: EntityType[],
    id: string,
): { entity?: EntityType; name?: string } => {
    for (const entity of entityTypes) {
        if (entity.id === id) {
            return { entity, name: entity.name }
        }

        if (entity.subEntityTypes.length > 0) {
            const result = findEntityById(entity.subEntityTypes, id)
            if (result.entity) {
                return result
            }
        }
    }

    return {}
}

/**
 * This will find any entity at any nested level
 * @param entityTypes
 * @param name
 * @returns
 */
export const findEntityByName = (
    entityTypes: EntityType[],
    name: string,
): { entity?: EntityType; id?: string } => {
    for (const entity of entityTypes) {
        if (entity.name === name) {
            return { entity, id: entity.id }
        }

        if (entity.subEntityTypes.length > 0) {
            const result = findEntityByName(entity.subEntityTypes, name)
            if (result.entity) {
                return result
            }
        }
    }

    return {}
}

export const flattenEntityTemplates = (
    entityTemplates: EntityType[],
): EntityType[] => {
    const allSubEntityTypes: EntityType[] = []

    const traverse = (entityTemplates: EntityType[]) => {
        for (const entityTemplate of entityTemplates) {
            allSubEntityTypes.push(entityTemplate)
            if (entityTemplate.subEntityTypes) {
                traverse(entityTemplate.subEntityTypes)
            }
        }
    }

    traverse(entityTemplates)
    return allSubEntityTypes
}

export const flattenRelatedEntities = (
    relatedEntitiesResponse: IEntityInstanceRelationsResponse,
): RelatedEntityInstance[] => {
    return Object.values(relatedEntitiesResponse.relations ?? {}).flat()
}

export const preserveExpandedState = (
    oldEntityTemplates: EntityType[],
    newEntityTemplates: EntityType[],
): EntityType[] => {
    const updateExpandedState = (
        oldList: EntityType[],
        newList: EntityType[],
    ): EntityType[] => {
        return newList.map((newEntity) => {
            // Find the corresponding old entity by id
            const oldEntity = oldList.find(
                (oldEntity) => oldEntity.id === newEntity.id,
            )

            // Return a new object with updated `isOpen` and recursively updated sub-entities
            return {
                ...newEntity,
                isOpen: oldEntity?.isOpen ?? newEntity.isOpen,
                subEntityTypes: newEntity.subEntityTypes?.length
                    ? updateExpandedState(
                          oldEntity?.subEntityTypes || [],
                          newEntity.subEntityTypes,
                      )
                    : newEntity.subEntityTypes,
            }
        })
    }

    return updateExpandedState(oldEntityTemplates, newEntityTemplates)
}

export const getEntityTableFilters = (
    allTableFilters: ITableFilters[],
    entityTemplateId: string,
    applicationId: string | undefined,
    tabIndex: number | string | undefined,
): Record<string, IFilterValue> => {
    return (
        allTableFilters.find(
            (i) =>
                i.entityTemplateId === entityTemplateId &&
                i.applicationId === applicationId &&
                i.tabIndex === tabIndex,
        )?.allAttributesFilters || ({} as Record<string, IFilterValue>)
    )
}

export const getEntityTableFiltersIndex = (
    allTableFilters: ITableFilters[],
    entityTemplateId: string,
    applicationId: string | undefined,
    tabIndex: number | string | undefined,
): number => {
    return allTableFilters.findIndex(
        (i) =>
            i.entityTemplateId === entityTemplateId &&
            i.applicationId === applicationId &&
            i.tabIndex === tabIndex,
    )
}

export const getEntityTableGlobalSearchTextFilter = (
    allTableFilters: ITableFilters[],
    entityTemplateId: string,
    applicationId: string | undefined,
    tabIndex: number | string | undefined,
): IGlobalSearchTextFilter | undefined => {
    return allTableFilters.find(
        (i) =>
            i.entityTemplateId === entityTemplateId &&
            i.applicationId === applicationId &&
            i.tabIndex === tabIndex,
    )?.gloalSearchText
}

export const getEntityTableEntityStatusFilter = (
    allTableFilters: ITableFilters[],
    entityTemplateId: string,
    applicationId: string | undefined,
    tabIndex: number | string | undefined,
): IEntityStatusFilter | undefined => {
    return allTableFilters.find(
        (i) =>
            i.entityTemplateId === entityTemplateId &&
            i.applicationId === applicationId &&
            i.tabIndex === tabIndex,
    )?.entityStatusFilter
}

export const canSelect = (item: any | undefined): boolean => {
    return (
        item?.action === TemplatePermissionMode.WRITE ||
        item?.action === TemplatePermissionMode.READ_WRITE
    )
}

export const canEditEntity = (
    item: IEntityRecord | undefined,
    isSuperAdmin = false,
): boolean => {
    return (
        isSuperAdmin ||
        item?.action === TemplatePermissionMode.WRITE ||
        item?.action === TemplatePermissionMode.READ_WRITE
    )
}

/**
 * UBQ-5027: a "Completed" device in Device Manager is an archived entity.
 * Completed devices are read-only, so the edit action must not be offered.
 */
export const isCompletedDevice = (
    item: Partial<Pick<IEntityRecord, 'status'>> | undefined,
): boolean => item?.status === EntityStatus.ARCHIVE

export const deriveColumnFiltersFromStore = (
    allAttributesFilters: Record<string, IFilterValue>,
    columnVisibilityList: ColumnOrderListType[],
): ColumnFilters[] => {
    return Object.entries(allAttributesFilters).reduce((acc, [key, obj]) => {
        if (obj.isColumnLevelFilter) {
            const dataType =
                columnVisibilityList.find((c) => c.id === key)?.dataType || ''
            if (obj.value?.length === 0) {
                return acc
            } else if (
                dataType.toLowerCase() ===
                    AttributeType.SENSOR_SNAPSHOT_READINGS ||
                dataType.toLowerCase() === AttributeType.CREATION_DATETIME
            ) {
                if (!obj?.value?.[0] && !obj?.value?.[1]) {
                    return acc
                }
            }
            acc.push({
                id: key,
                value: obj.value,
            })
        }
        return acc
    }, [] as ColumnFilters[])
}

export const getNonColumnLevelFilters = (
    filter: ITableFilters,
): Record<string, IFilterValue> => {
    return Object.entries(filter.allAttributesFilters).reduce(
        (acc, [attributeName, filterValue]) => {
            if (!filterValue.isColumnLevelFilter) {
                acc[attributeName] = filterValue
            }
            return acc
        },
        {} as Record<string, IFilterValue>,
    )
}
